/**
 * SMS settings (ADR-099 in platform): how a tenant's texts leave, set on one screen.
 *
 * An admin picks the provider and types the account's keys. core-api asks Twilio about the keys when
 * they are saved, makes the two triggers Twilio calls, and gives back their URLs whole. The token is
 * write-only: the console sends one and never reads one. A check reads the Twilio account and says
 * what is still wrong.
 *
 * What the form refuses here follows core-api's `SmsSettingsUpdate` and `sms_settings.save`, so the
 * usual mistakes are said beside the field and not as a 422. Pure, for `test/sms-settings.test.ts`.
 */

export type SmsProvider = "off" | "twilio" | "sandbox";

/** One of the two triggers the settings made, as core-api shows it. */
export interface SmsHook {
  trigger_id: number;
  name: string;
  enabled: boolean;
  /** The whole URL Twilio calls, secret included. Null when the trigger can no longer take the call. */
  url: string | null;
  last_received_at: string | null;
  /** Why the trigger no longer does its work, after a change on the Triggers page. */
  problem: string | null;
}

export interface SmsNumber {
  /** The inventory row. */
  id: number;
  number: string;
  /** Whether the number is switched on to send texts. */
  sms: boolean;
  /** Where a text sent to it lands. */
  inbox: "campaign" | "rep" | "unassigned" | string;
  campaign_name: string | null;
  rep_name: string | null;
}

export interface SmsSettings {
  tenant_id: number;
  provider: SmsProvider | string;
  twilio_account_sid: string | null;
  /** Whether a token is kept. The token itself is never in an answer. */
  twilio_auth_token_set: boolean;
  /** What Twilio calls the account, as of the last time it accepted the keys. */
  twilio_account_name: string | null;
  twilio_messaging_service_sid: string | null;
  daily_limit: number;
  public_url: string | null;
  incoming: SmsHook | null;
  reports: SmsHook | null;
  numbers: SmsNumber[];
  checked_at: string | null;
  updated_at: string | null;
}

/** What `PUT /integrations/sms` takes: every setting, each time. The token only when one was typed. */
export interface SmsSettingsInput {
  provider: SmsProvider;
  twilio_account_sid: string | null;
  twilio_auth_token?: string;
  twilio_messaging_service_sid: string | null;
  daily_limit: number;
  public_url: string | null;
  /** Superadmin only. */
  tenant_id?: number;
}

export interface SmsCheckItem {
  /** provider | address | incoming | reports | account | messaging_service |
   *  messaging_service_incoming | numbers | number */
  code: string;
  ok: boolean;
  detail: string;
  /** The number of ours a `number` item is about. */
  number: string | null;
}

export interface SmsCheck {
  ok: boolean;
  checked_at: string;
  items: SmsCheckItem[];
}

/** The form: every field as the text in its input. */
export interface SmsValues {
  provider: SmsProvider;
  accountSid: string;
  /** Typed now. Empty keeps the token already kept. */
  authToken: string;
  serviceSid: string;
  publicUrl: string;
  dailyLimit: string;
}

export const SMS_PROVIDERS: { value: SmsProvider; label: string; description: string }[] = [
  { value: "off", label: "Off", description: "Nothing is sent. Texts to your numbers still arrive." },
  { value: "twilio", label: "Twilio", description: "Texts leave through your Twilio account." },
  {
    value: "sandbox",
    label: "Sandbox",
    description: "Every text is taken and none is sent. For a test stack.",
  },
];

/** Which parts of the page belong on it for the provider that is picked. */
export interface SmsSections {
  /** The Twilio account's fields. */
  account: boolean;
  /** The two URLs Twilio calls. */
  urls: boolean;
  /** The tenant's numbers, and which of them send. */
  numbers: boolean;
  /** The check of the Twilio account. */
  check: boolean;
  /** One line under the picker: what the provider does, or what saving it will bring. */
  note: string | null;
}

/**
 * What the page shows for the provider picked in the form. Only what that provider has a use for:
 *
 * - Off: the picker and nothing else. Nothing is sent, so there is no account to type, no number
 *   to switch on and nothing to check.
 * - Sandbox: the numbers, because a text still leaves only from a number that is switched on. No
 *   account, no URLs and no check: nothing reaches a provider.
 * - Twilio: the account's fields. The URLs, the numbers and the check come once it is saved, in
 *   the order they are done in, since each of them needs the saved account.
 *
 * The parts under the form are about the settings as they are saved. So while the provider picked
 * is not the saved one yet, none of them shows, and the note says what saving brings.
 */
export function smsSections(picked: SmsProvider, settings: SmsSettings): SmsSections {
  const live = picked === settings.provider;
  const kept = settings.twilio_auth_token_set;
  const hasUrls = settings.incoming !== null || settings.reports !== null;
  let note: string | null;
  if (picked === "twilio") {
    note = live
      ? null
      : "Save, and the next steps show here: the URL to paste into Twilio, your numbers, and a check of the account.";
  } else if (picked === "sandbox") {
    note = live
      ? "A text sent from the console is checked and kept in its thread as usual, and reads as sent. Nothing reaches a phone."
      : "Save, and every text is taken and none is sent. Your numbers show here, to switch on the ones that send.";
  } else if (live) {
    note = kept
      ? "A send is refused, saying texting is switched off. The Twilio account is kept for when you switch back."
      : "A send is refused, saying texting is switched off. Pick Twilio to set it up.";
  } else {
    note = kept
      ? "Save to switch sending off. The Twilio account and its URLs are kept for when you switch back."
      : "Save to switch sending off.";
  }
  return {
    account: picked === "twilio",
    urls: live && picked === "twilio" && hasUrls,
    numbers: live && picked !== "off",
    check: live && picked === "twilio",
    note,
  };
}

export const DEFAULT_DAILY_LIMIT = 10_000;
export const MAX_DAILY_LIMIT = 1_000_000;
const ACCOUNT_SID = /^AC[0-9a-fA-F]{32}$/;
const SERVICE_SID = /^MG[0-9a-fA-F]{32}$/;

function isProvider(value: string): value is SmsProvider {
  return value === "off" || value === "twilio" || value === "sandbox";
}

/**
 * Where Twilio reaches this system, as a first guess: the address the console calls core-api at,
 * when that is a public https address. On AWS it is the console's own origin with `/api`. On a
 * laptop it is `http://localhost`, which Twilio cannot reach, so the field starts empty and the
 * admin types the tunnel's address.
 */
export function defaultPublicUrl(apiBase: string, origin: string): string {
  const absolute = /^https?:\/\//i.test(apiBase) ? apiBase : `${origin.replace(/\/$/, "")}${apiBase}`;
  const url = absolute.replace(/\/+$/, "");
  return publicUrlProblem(url) === null ? url : "";
}

/** The form for the settings core-api holds. `fallbackPublicUrl` fills an address nobody has set. */
export function smsValues(settings: SmsSettings, fallbackPublicUrl = ""): SmsValues {
  return {
    provider: isProvider(settings.provider) ? settings.provider : "off",
    accountSid: settings.twilio_account_sid ?? "",
    authToken: "",
    serviceSid: settings.twilio_messaging_service_sid ?? "",
    publicUrl: settings.public_url ?? fallbackPublicUrl,
    dailyLimit: String(settings.daily_limit),
  };
}

/** Why an address cannot be the one Twilio reaches this system at, or null. As core-api's
 *  `public_url_problem`. */
export function publicUrlProblem(url: string): string | null {
  let parts: URL;
  try {
    parts = new URL(url);
  } catch {
    return "The public address is a URL, like https://console.example.com/api.";
  }
  if (parts.protocol !== "https:") {
    return "The public address must start with https://. A trigger's secret travels in it.";
  }
  if (!parts.hostname) return "The public address needs a host name.";
  if (parts.username || parts.password) {
    return "The public address takes no user name or password. The secret is added to it.";
  }
  if (parts.search || parts.hash || /[\s?#]/.test(url)) {
    return "The public address is the part before /hooks: no ? and no # in it.";
  }
  return null;
}

function limitOf(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d{1,7}$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value <= MAX_DAILY_LIMIT ? value : null;
}

/**
 * Why the form cannot be saved as it stands, as a sentence for the admin, or null. Each field is
 * checked for what it is, and Twilio needs its account and the public address.
 *
 * Only Twilio has fields. With Off or the sandbox picked they are not on the page and are not sent
 * (`smsPayload`), so nothing typed in them can stand in the way of switching the sending off.
 *
 * The token belongs to its account: another Account SID needs its own, so a changed SID with no
 * token typed is refused here as core-api refuses it.
 */
export function smsProblem(values: SmsValues, settings: SmsSettings): string | null {
  if (values.provider !== "twilio") return null;
  const sid = values.accountSid.trim();
  const token = values.authToken.trim();
  const service = values.serviceSid.trim();
  const address = values.publicUrl.trim();

  if (sid && !ACCOUNT_SID.test(sid)) {
    return "The Account SID starts with AC and has 32 letters and digits after it.";
  }
  if (/\s/.test(token)) return "The Auth Token has no spaces in it.";
  if (service && !SERVICE_SID.test(service)) {
    return "The Messaging Service SID starts with MG and has 32 letters and digits after it.";
  }
  if (limitOf(values.dailyLimit) === null) {
    return `The daily limit is a whole number from 0 to ${MAX_DAILY_LIMIT.toLocaleString("en-US")}. 0 is no limit.`;
  }
  if (address) {
    const problem = publicUrlProblem(address.replace(/\/+$/, ""));
    if (problem) return problem;
  }
  const kept = settings.twilio_auth_token_set && settings.twilio_account_sid === sid;
  if (sid && !token && !kept) {
    return settings.twilio_account_sid && settings.twilio_account_sid !== sid
      ? "Enter the Auth Token of this account. A token is kept for one account."
      : "Enter the account's Auth Token.";
  }
  if (!sid) return "Twilio needs the account's SID and its Auth Token.";
  if (!address) {
    return "Twilio needs the public address of this system: where it posts texts and reports.";
  }
  return null;
}

/**
 * What `PUT /integrations/sms` is sent for the form. core-api takes every setting each time, so:
 *
 * - With Twilio picked, the fields as typed, and the token only when one was typed.
 * - With Off or the sandbox picked, the provider, and everything else as core-api already holds
 *   it. The fields are not on the page then, so whatever was typed in them before the provider
 *   was changed is not saved by a save that only switches the sending off.
 */
export function smsPayload(values: SmsValues, settings: SmsSettings): SmsSettingsInput {
  if (values.provider !== "twilio") {
    return {
      provider: values.provider,
      twilio_account_sid: settings.twilio_account_sid,
      twilio_messaging_service_sid: settings.twilio_messaging_service_sid,
      daily_limit: settings.daily_limit,
      public_url: settings.public_url,
    };
  }
  const token = values.authToken.trim();
  const address = values.publicUrl.trim().replace(/\/+$/, "");
  return {
    provider: values.provider,
    twilio_account_sid: values.accountSid.trim() || null,
    ...(token ? { twilio_auth_token: token } : {}),
    twilio_messaging_service_sid: values.serviceSid.trim() || null,
    daily_limit: limitOf(values.dailyLimit) ?? DEFAULT_DAILY_LIMIT,
    public_url: address || null,
  };
}

/**
 * Whether saving the form would change anything. With Twilio picked, any field that differs from
 * what was loaded, and a typed token always. With Off or the sandbox picked, only the provider:
 * the fields are not on the page and are not sent.
 */
export function smsChanged(values: SmsValues, loaded: SmsValues): boolean {
  if (values.provider !== "twilio") return values.provider !== loaded.provider;
  return (
    values.provider !== loaded.provider ||
    values.accountSid.trim() !== loaded.accountSid.trim() ||
    values.authToken.trim() !== "" ||
    values.serviceSid.trim() !== loaded.serviceSid.trim() ||
    values.publicUrl.trim().replace(/\/+$/, "") !== loaded.publicUrl.trim().replace(/\/+$/, "") ||
    values.dailyLimit.trim() !== loaded.dailyLimit.trim()
  );
}

/**
 * A trigger's URL with its secret dotted out, for the screen: the copy button gives the whole one.
 * The secret is the password in the URL, after the user name.
 */
export function maskedUrl(url: string): string {
  return url.replace(/^(https?:\/\/[^:/@]+:)[^@/]+@/i, "$1••••••••@");
}

/** Where a text sent to one of our numbers lands, in the words the Numbers page uses. */
export function inboxText(number: Pick<SmsNumber, "inbox" | "campaign_name" | "rep_name">): string {
  if (number.inbox === "campaign") return number.campaign_name ?? "A campaign";
  if (number.inbox === "rep") {
    const rep = number.rep_name ?? "A rep";
    return number.campaign_name ? `${rep}, on ${number.campaign_name}` : rep;
  }
  return "Unassigned";
}

/** One line for a check's result: that everything is right, or how many things are not. The Email
 *  settings' check says it the same way. */
export function checkSummary(check: { ok: boolean; items: readonly { ok: boolean }[] }): string {
  const wrong = check.items.filter((item) => !item.ok).length;
  if (wrong === 0) return "Everything is ready.";
  return wrong === 1 ? "1 thing needs attention." : `${wrong} things need attention.`;
}
